import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Col, Row, Spinner } from "reactstrap";
import {
  FiBell,
  FiBox,
  FiCheck,
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiExternalLink,
  FiFileText,
  FiFilter,
  FiInbox,
  FiSearch,
  FiShoppingBag,
  FiTrash2,
  FiTruck,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import BackButton from "../../components/button/BackButton";
import PageName from "../../components/react_helmet/PageName";
import {
  deleteNotification,
  getCategoryMeta,
  markAllNotificationsAsRead,
  markNotificationAsRead,
  markNotificationAsUnread,
  normalizeNotificationTimestamp,
} from "../../utils/notificationService";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";

const CATEGORY_TABS = [
  { key: "all", label: "All", icon: FiBell },
  { key: "order", label: "Sales Orders", icon: FiShoppingBag },
  { key: "purchase_order", label: "Purchase Orders", icon: FiFileText },
  { key: "quote", label: "Quotes", icon: FiDollarSign },
  { key: "product", label: "Products", icon: FiBox },
  { key: "customer_vendor", label: "Customers & Vendors", icon: FiUsers },
  { key: "other", label: "System & Other", icon: FiCheckCircle },
];

const Notifications = () => {
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);
  const { notifications } = useSelector((state) => state.redux);

  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all' | 'unread' | 'read'
  const [visibleCount, setVisibleCount] = useState(15);
  const [processingId, setProcessingId] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);

  // Filter raw notifications based on user role (Admin/Super-admin vs regular user)
  const scopedNotifications = useMemo(() => {
    if (!notifications || !Array.isArray(notifications)) return [];
    const isAdmin = user?.role === "Admin" || user?.role === "Super-admin";

    return notifications.filter((i) => {
      if (isAdmin) {
        // Admins can see user-initiated requests, admin broadcasts, category updates, and general updates
        return (
          i?.type === "user" ||
          i?.targetRole === "admin" ||
          i?.targetRole === "all" ||
          !i?.type
        );
      }
      // Non-admins see admin-directed responses or items targeted to their user ID
      return (
        (i?.type === "admin" && i?.userId === user?._id) ||
        i?.userId === user?._id
      );
    });
  }, [notifications, user]);

  // Overall unread count for the active user
  const unreadCount = useMemo(() => {
    return scopedNotifications.filter((i) => !i.read).length;
  }, [scopedNotifications]);

  // Apply search, category, and read/unread filters
  const filteredNotifications = useMemo(() => {
    let list = [...scopedNotifications];

    // Status filter
    if (statusFilter === "unread") {
      list = list.filter((i) => !i.read);
    } else if (statusFilter === "read") {
      list = list.filter((i) => !!i.read);
    }

    // Category filter
    if (selectedCategory !== "all") {
      if (selectedCategory === "customer_vendor") {
        list = list.filter(
          (i) => i.category === "customer" || i.category === "vendor"
        );
      } else if (selectedCategory === "other") {
        list = list.filter(
          (i) =>
            i.category === "system" ||
            i.category === "leave" ||
            !i.category ||
            !["order", "purchase_order", "quote", "product", "customer", "vendor"].includes(i.category)
        );
      } else {
        list = list.filter((i) => i.category === selectedCategory);
      }
    }

    // Search filter
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((i) => {
        const messageMatch = (i.message || "").toLowerCase().includes(q);
        const titleMatch = (i.title || "").toLowerCase().includes(q);
        const entityMatch = (i.entityId || "").toLowerCase().includes(q);
        const actorMatch = (i.actor?.name || "").toLowerCase().includes(q);
        return messageMatch || titleMatch || entityMatch || actorMatch;
      });
    }

    // Sort descending by timestamp
    return list.sort((a, b) => {
      const timeA = normalizeNotificationTimestamp(a.createdAt);
      const timeB = normalizeNotificationTimestamp(b.createdAt);
      return timeB - timeA;
    });
  }, [scopedNotifications, statusFilter, selectedCategory, search]);

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + 15);
  };

  const handleToggleRead = async (item, e) => {
    e?.stopPropagation();
    const id = item._id || item.id;
    if (!id) return;
    setProcessingId(id);
    try {
      if (item.read) {
        await markNotificationAsUnread(id);
        showSuccessToast("Marked as unread");
      } else {
        await markNotificationAsRead(id);
        showSuccessToast("Marked as read");
      }
    } catch (err) {
      showErrorToast("Failed to update status");
    } finally {
      setProcessingId(null);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;
    setMarkingAll(true);
    try {
      await markAllNotificationsAsRead(scopedNotifications);
      showSuccessToast("All notifications marked as read");
    } catch (err) {
      showErrorToast("Failed to mark all as read");
    } finally {
      setMarkingAll(false);
    }
  };

  const handleDelete = async (item, e) => {
    e?.stopPropagation();
    const id = item._id || item.id;
    if (!id) return;
    setProcessingId(id);
    try {
      await deleteNotification(id);
      showSuccessToast("Notification removed");
    } catch (err) {
      showErrorToast("Failed to delete notification");
    } finally {
      setProcessingId(null);
    }
  };

  const handleItemClick = (item) => {
    const id = item._id || item.id;
    if (!item.read && id) {
      markNotificationAsRead(id).catch(console.error);
    }
    if (item.targetUrl) {
      history.push(item.targetUrl);
    }
  };

  const renderCategoryIcon = (category) => {
    switch (category) {
      case "order":
        return <FiShoppingBag size={16} />;
      case "purchase_order":
        return <FiFileText size={16} />;
      case "quote":
        return <FiDollarSign size={16} />;
      case "product":
        return <FiBox size={16} />;
      case "customer":
        return <FiUsers size={16} />;
      case "vendor":
        return <FiTruck size={16} />;
      default:
        return <FiBell size={16} />;
    }
  };

  return (
    <>
      <PageName title="Notifications" />
      <Row className="justify-content-center pt-3 pb-5">
        <Col md={11} lg={9}>
          {/* Header Row */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center gap-2">
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "10px",
                  backgroundColor: "rgba(0, 80, 157, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--bg-primary-color, #00509d)",
                }}
              >
                <FiBell size={22} />
              </div>
              <div>
                <h4 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                  Notifications
                  {unreadCount > 0 && (
                    <span
                      className="badge rounded-pill bg-danger"
                      style={{ fontSize: "11px", fontWeight: "600" }}
                    >
                      {unreadCount} unread
                    </span>
                  )}
                </h4>
                <small className="text-muted" style={{ fontSize: "12px" }}>
                  Real-time updates, activity log, and system alerts
                </small>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllAsRead}
                  disabled={markingAll}
                  className="btn btn-outline-primary btn-sm px-3 rounded-pill fw-semibold d-flex align-items-center gap-1"
                  style={{ fontSize: "12px" }}
                >
                  {markingAll ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <FiCheck size={14} /> Mark all read
                    </>
                  )}
                </button>
              )}
              <BackButton handleClick={() => history.goBack()} />
            </div>
          </div>

          {/* Controls Bar: Search & Status Filter */}
          <div className="bg-white rounded-3 shadow-sm border p-3 mb-3">
            <div className="row g-2 align-items-center">
              <div className="col-md-7 col-12">
                <div className="position-relative">
                  <FiSearch
                    size={16}
                    className="position-absolute text-muted"
                    style={{ left: "12px", top: "50%", transform: "translateY(-50%)" }}
                  />
                  <input
                    type="search"
                    className="form-control form-control-sm"
                    placeholder="Search by keyword, order #, PO #, or author..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{
                      paddingLeft: "36px",
                      borderRadius: "8px",
                      fontSize: "13px",
                      height: "36px",
                    }}
                  />
                </div>
              </div>

              {/* Status Filter Pills */}
              <div className="col-md-5 col-12 d-flex justify-content-md-end gap-1">
                <div
                  className="btn-group btn-group-sm w-100 w-md-auto"
                  role="group"
                  aria-label="Filter status"
                >
                  <button
                    type="button"
                    className={`btn ${
                      statusFilter === "all" ? "btn-primary" : "btn-outline-secondary"
                    }`}
                    style={{ fontSize: "12px", fontWeight: "600" }}
                    onClick={() => setStatusFilter("all")}
                  >
                    All ({scopedNotifications.length})
                  </button>
                  <button
                    type="button"
                    className={`btn ${
                      statusFilter === "unread" ? "btn-primary" : "btn-outline-secondary"
                    }`}
                    style={{ fontSize: "12px", fontWeight: "600" }}
                    onClick={() => setStatusFilter("unread")}
                  >
                    Unread ({unreadCount})
                  </button>
                  <button
                    type="button"
                    className={`btn ${
                      statusFilter === "read" ? "btn-primary" : "btn-outline-secondary"
                    }`}
                    style={{ fontSize: "12px", fontWeight: "600" }}
                    onClick={() => setStatusFilter("read")}
                  >
                    Read ({scopedNotifications.length - unreadCount})
                  </button>
                </div>
              </div>
            </div>

            {/* Category Filter Pills */}
            <div
              className="d-flex align-items-center gap-1 mt-3 pt-2 border-top overflow-auto"
              style={{ whiteSpace: "nowrap" }}
            >
              <span
                className="text-muted fw-bold me-2 d-flex align-items-center gap-1"
                style={{ fontSize: "11px", textTransform: "uppercase" }}
              >
                <FiFilter size={12} /> Category:
              </span>
              {CATEGORY_TABS.map((tab) => {
                const Icon = tab.icon;
                const isSelected = selectedCategory === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setSelectedCategory(tab.key)}
                    className={`btn btn-sm rounded-pill px-3 py-1 d-inline-flex align-items-center gap-1 ${
                      isSelected
                        ? "btn-primary text-white"
                        : "btn-light text-secondary border"
                    }`}
                    style={{
                      fontSize: "12px",
                      fontWeight: isSelected ? "600" : "500",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <Icon size={13} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notifications Feed */}
          <div className="bg-white rounded-4 shadow-sm border overflow-hidden">
            {filteredNotifications?.length > 0 ? (
              <div className="list-group list-group-flush">
                {filteredNotifications.slice(0, visibleCount).map((item, index) => {
                  const meta = getCategoryMeta(item.category);
                  const isUnread = !item.read;
                  const itemTimestamp = normalizeNotificationTimestamp(item.createdAt);
                  const hasTarget = !!item.targetUrl;

                  return (
                    <div
                      key={item._id || item.id || index}
                      onClick={() => handleItemClick(item)}
                      className={`list-group-item list-group-item-action p-3 d-flex align-items-start gap-3 border-bottom ${
                        isUnread ? "bg-light-subtle" : ""
                      }`}
                      style={{
                        cursor: hasTarget ? "pointer" : "default",
                        backgroundColor: isUnread ? "rgba(0, 80, 157, 0.03)" : "#ffffff",
                        borderLeft: isUnread
                          ? "4px solid var(--bg-primary-color, #00509d)"
                          : "4px solid transparent",
                        transition: "background-color 0.15s ease",
                      }}
                    >
                      {/* Category Icon Badge */}
                      <div
                        style={{
                          width: "38px",
                          height: "38px",
                          borderRadius: "10px",
                          backgroundColor: meta.bg,
                          color: meta.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          marginTop: "2px",
                        }}
                        title={meta.label}
                      >
                        {renderCategoryIcon(item.category)}
                      </div>

                      {/* Content Body */}
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                          {item.title ? (
                            <span
                              className={`fw-bold ${
                                isUnread ? "text-dark" : "text-secondary"
                              }`}
                              style={{ fontSize: "14px" }}
                            >
                              {item.title}
                            </span>
                          ) : (
                            <span
                              className="fw-bold text-dark"
                              style={{ fontSize: "14px" }}
                            >
                              {meta.label} Update
                            </span>
                          )}

                          {/* Category Badge */}
                          <span
                            className="badge rounded-pill"
                            style={{
                              backgroundColor: meta.bg,
                              color: meta.color,
                              fontSize: "10px",
                              fontWeight: "600",
                              padding: "3px 8px",
                            }}
                          >
                            {meta.label}
                          </span>

                          {/* Entity Identifier */}
                          {item.entityId && (
                            <span
                              className="badge bg-secondary-subtle text-secondary"
                              style={{ fontSize: "10px", fontWeight: "600" }}
                            >
                              #{item.entityId}
                            </span>
                          )}

                          {/* Unread indicator dot */}
                          {isUnread && (
                            <span
                              className="badge bg-primary rounded-pill ms-auto"
                              style={{ fontSize: "9px", padding: "3px 6px" }}
                            >
                              NEW
                            </span>
                          )}
                        </div>

                        {/* Message Description */}
                        <div
                          className="text-dark mb-2"
                          style={{
                            fontSize: "13px",
                            lineHeight: "1.5",
                            fontWeight: isUnread ? "500" : "400",
                          }}
                        >
                          {item.message || "Notification details"}
                        </div>

                        {/* Meta details & Timestamp */}
                        <div
                          className="d-flex align-items-center gap-3 text-muted flex-wrap"
                          style={{ fontSize: "11px" }}
                        >
                          <span
                            className="d-flex align-items-center gap-1"
                            title={moment(itemTimestamp).format("YYYY-MM-DD HH:mm:ss")}
                          >
                            <FiClock size={12} />
                            {itemTimestamp
                              ? moment(itemTimestamp).fromNow()
                              : "Just now"}
                          </span>

                          {item.actor?.name && (
                            <span className="d-flex align-items-center gap-1">
                              <FiUser size={12} />
                              by <strong className="text-dark">{item.actor.name}</strong>
                              {item.actor.role && ` (${item.actor.role})`}
                            </span>
                          )}

                          {itemTimestamp && (
                            <span className="text-muted d-none d-md-inline">
                              &bull; {moment(itemTimestamp).format("MMM Do YYYY, h:mm a")}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div
                        className="d-flex align-items-center gap-1 flex-shrink-0 ms-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {hasTarget && (
                          <button
                            type="button"
                            onClick={() => history.push(item.targetUrl)}
                            className="btn btn-outline-primary btn-sm px-2 py-1 rounded"
                            title="Go to details"
                            style={{ fontSize: "11px", fontWeight: "600" }}
                          >
                            <FiExternalLink size={13} className="me-1" />
                            View
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={(e) => handleToggleRead(item, e)}
                          disabled={processingId === (item._id || item.id)}
                          className={`btn btn-sm px-2 py-1 rounded ${
                            isUnread
                              ? "btn-light text-primary"
                              : "btn-light text-muted"
                          }`}
                          title={isUnread ? "Mark as read" : "Mark as unread"}
                        >
                          <FiCheck size={14} />
                        </button>

                        <button
                          type="button"
                          onClick={(e) => handleDelete(item, e)}
                          disabled={processingId === (item._id || item.id)}
                          className="btn btn-sm btn-light text-danger px-2 py-1 rounded"
                          title="Delete notification"
                        >
                          <FiTrash2 size={13} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-5">
                <FiInbox size={54} className="text-muted opacity-50 mb-3" />
                <h5 className="text-dark fw-bold mb-1">No Notifications Found</h5>
                <p className="text-secondary small mb-0">
                  {search.trim() || selectedCategory !== "all" || statusFilter !== "all"
                    ? "No notifications match your current search and filter criteria."
                    : "You're all caught up! New updates will appear here automatically in real time."}
                </p>
                {(search.trim() || selectedCategory !== "all" || statusFilter !== "all") && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearch("");
                      setSelectedCategory("all");
                      setStatusFilter("all");
                    }}
                    className="btn btn-outline-secondary btn-sm rounded-pill mt-3 px-3 fw-semibold"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            )}

            {/* Load More Button */}
            {filteredNotifications?.length > visibleCount && (
              <div className="d-flex justify-content-center align-items-center p-3 border-top bg-light gap-2">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  className="btn btn-outline-primary btn-sm px-4 rounded-pill fw-bold"
                >
                  Load More ({filteredNotifications.length - visibleCount} remaining)
                </button>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </>
  );
};

export default Notifications;
